import { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Assessment,
  BarChart as BarIcon,
  Category,
  CheckCircle,
  LocalFireDepartment,
  Recommend,
  Refresh,
  Shield,
  ShowChart,
  Speed,
  Timeline,
  TrendingDown,
  TrendingUp,
  WaterDrop,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { colors, status } from '../../theme';
import {
  Card,
  CardTitle,
  MiniScoreBar,
  SETUP_COLORS,
  SectorTableSkeleton,
  Tile,
  TileSkeleton,
  fmtDec,
  getDisplayText,
  getDisplayTone,
  scoreColor,
  tdSx,
  thSx,
  trSx,
} from './shared';

export default function OverviewSection({
  isFirstLoad,
  summary,
  regime,
  signals,
  recommendations,
  loading,
  scanning,
  onRefresh,
  onScan,
  sectors,
  setupDist,
  regimeHistory,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const buyNowCount = recommendations?.summary?.find((item) => item.status === 'READY')?.count || 0;
  const regimeDisplay = regime?.display || {};
  const regimeTone = getDisplayTone(regimeDisplay.tone);
  const RegimeIcon = regimeDisplay.icon === 'trending_up' ? TrendingUp : regimeDisplay.icon === 'trending_down' ? TrendingDown : ShowChart;

  const chartData = useMemo(() => {
    if (!regimeHistory?.length) return { data: [], yMin: 0, yMax: 100 };
    const reversed = [...regimeHistory].reverse();
    const scores = reversed.map((item) => item.market_score).filter(Boolean);
    if (!scores.length) return { data: reversed, yMin: 0, yMax: 100 };
    const min = Math.floor(Math.min(...scores) / 10) * 10;
    const max = Math.ceil(Math.max(...scores) / 10) * 10;
    return { data: reversed, yMin: Math.max(0, min), yMax: Math.min(100, max) };
  }, [regimeHistory]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 1.5, background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)' }}>
            <Assessment sx={{ fontSize: 22, color: colors.background.paper }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: colors.gray[800], letterSpacing: -0.3 }}>{t('vsa.title')}</Typography>
              {summary?.scan_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: colors.gray[100], px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.info.main, boxShadow: `0 0 6px ${colors.info.main}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: colors.gray[600] }}>{summary.scan_date}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: colors.gray[400], fontWeight: 500 }}>{t('vsa.subtitle')}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
            disabled={loading || scanning}
            onClick={onRefresh}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px', bgcolor: colors.info.main, color: colors.background.paper, px: 2, '&:hover': { bgcolor: colors.info.dark }, '&:disabled': { bgcolor: colors.gray[400], color: colors.background.paper } }}
          >
            {loading ? t('vsa.actions.loading') : t('vsa.actions.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={scanning ? <CircularProgress size={16} color="inherit" /> : <Assessment sx={{ fontSize: 16 }} />}
            disabled={loading || scanning}
            onClick={onScan}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px', bgcolor: colors.warning.main, color: colors.background.paper, px: 2, '&:hover': { bgcolor: colors.warning.dark }, '&:disabled': { bgcolor: colors.gray[400], color: colors.background.paper } }}
          >
            {scanning ? t('vsa.actions.scanning') : t('vsa.actions.recalculate')}
          </Button>
        </Box>
      </Box>

      {isFirstLoad ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2.5 }}>
          {[0, 1, 2, 3, 4].map((index) => <TileSkeleton key={index} />)}
        </Box>
      ) : (summary && regime) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2.5 }}>
          <Tile label={t('vsa.tiles.bestSignals')} value={summary.grade_a || 0} sub={t('vsa.tiles.bestSignalsSub')} color="#10b981" Icon={LocalFireDepartment} />
          <Tile label={t('vsa.tiles.readyToBuy')} value={buyNowCount} sub={t('vsa.tiles.readyToBuySub')} color="#10b981" Icon={CheckCircle} />
          <Tile label={t('vsa.tiles.totalSignals')} value={signals.length} sub={t('vsa.tiles.totalSignalsSub')} color="#3b82f6" Icon={Assessment} />
          <Tile label={t('vsa.tiles.avgScore')} value={Math.round(summary.avg_priority_score || 0)} sub={t('vsa.tiles.avgScoreSub')} color={scoreColor(summary.avg_priority_score)} Icon={Timeline} />
          <Tile label={t('vsa.tiles.strongRS')} value={`${(summary.pct_strong_rs || 0).toFixed(0)}%`} sub={t('vsa.tiles.strongRSSub')} color="#8b5cf6" Icon={TrendingUp} />
        </Box>
      )}

      {isFirstLoad ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 2, mb: 2.5 }}>
          <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton width={140} height={14} />
            </Box>
            <Box sx={{ px: 2, py: 2 }}><Skeleton variant="rounded" height={150} sx={{ borderRadius: 1 }} /></Box>
          </Box>
          <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton width={120} height={14} />
            </Box>
            <Box sx={{ px: 2, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2 }} />
                <Box><Skeleton width={90} height={16} sx={{ mb: 0.5 }} /><Skeleton width={120} height={10} /></Box>
              </Box>
              {[100, 85, 95, 80].map((width, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                  <Skeleton variant="circular" width={14} height={14} />
                  <Skeleton width={50} height={10} />
                  <Skeleton variant="rounded" width={width} height={6} sx={{ borderRadius: 1, flex: 1 }} />
                  <Skeleton width={20} height={10} />
                </Box>
              ))}
              <Skeleton variant="rounded" height={36} sx={{ borderRadius: 1.5, mt: 1 }} />
            </Box>
          </Box>
        </Box>
      ) : (summary && regime) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 2, mb: 2.5 }}>
          <Card sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardTitle icon={Timeline}>{t('vsa.chart.marketScoreTrend')}</CardTitle>
            {chartData.data.length > 0 ? (
              <Box sx={{ flex: 1, minHeight: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData.data}>
                    <defs>
                      <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.info.main} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={colors.info.main} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(value) => value.slice(5)} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 9 }} domain={[chartData.yMin, chartData.yMax]} width={28} />
                    <RTooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const value = payload[0].value;
                        const color = value >= 50 ? colors.success.main : value >= 40 ? colors.warning.main : colors.error.main;
                        return (
                          <Box sx={{ bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, borderRadius: 1.5, px: 1.5, py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                            <Typography sx={{ fontSize: '0.7rem', color: colors.gray[400], mb: 0.3 }}>{label}</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color }}>{t('vsa.chart.marketScore', { value: fmtDec(value, 1) })}</Typography>
                          </Box>
                        );
                      }}
                    />
                    <ReferenceLine y={50} stroke={colors.warning.main} strokeDasharray="4 4" />
                    <Area type="monotone" dataKey="market_score" stroke={colors.info.main} fill="url(#scoreGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.75rem', color: colors.gray[300] }}>{t('vsa.chart.noData')}</Typography>
              </Box>
            )}
          </Card>

          <Card>
            <CardTitle icon={Speed}>{t('vsa.regime.title')}</CardTitle>
            <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(regimeTone.color, 0.06), borderRadius: 2, border: `1px solid ${alpha(regimeTone.color, 0.15)}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: regimeTone.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RegimeIcon sx={{ fontSize: 22, color: '#fff' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: regimeTone.color }}>{getDisplayText(regimeDisplay, t, regime.market_regime, ['label_key', 'key'])}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>{t('vsa.regime.gateScore', { gate: fmtDec(regime.regime_gate, 1), score: fmtDec(regime.market_score, 1) })}</Typography>
              </Box>
            </Box>
            <MiniScoreBar label={t('vsa.regime.trend')} score={regime.trend_score} icon={TrendingUp} />
            <MiniScoreBar label={t('vsa.regime.breadth')} score={regime.breadth_score} icon={Category} />
            <MiniScoreBar label={t('vsa.regime.liquidity')} score={regime.liquidity_score} icon={WaterDrop} />
            <MiniScoreBar label={t('vsa.regime.risk')} score={regime.risk_score} icon={Shield} />
            {regime.recommendation && (
              <Box sx={{ mt: 1.5, p: 1.2, bgcolor: alpha(colors.info.main, 0.04), borderRadius: 1.5, border: `1px solid ${colors.gray[200]}` }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                  <Recommend sx={{ fontSize: 14, color: colors.info.main, mt: 0.2 }} />
                  <Typography sx={{ fontSize: '0.7rem', color: colors.gray[600], lineHeight: 1.5 }}>{getDisplayText({ key: regimeDisplay.desc_key }, t, regime.recommendation)}</Typography>
                </Box>
              </Box>
            )}
          </Card>
        </Box>
      )}

      {isFirstLoad ? (
        <SectorTableSkeleton />
      ) : summary && regime && (sectors.length > 0 || setupDist.length > 0) && (
        <Card sx={{ mb: 2, px: 0, pt: 2, pb: 0, overflow: 'hidden' }}>
          {setupDist.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: sectors.length > 0 ? 2 : 0, pb: sectors.length > 0 ? 1.5 : 0, borderBottom: sectors.length > 0 ? '1px solid #f1f5f9' : 'none', flexWrap: 'wrap', px: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Assessment sx={{ fontSize: 14, color: colors.gray[500] }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>{t('vsa.setup.title')}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 100px', display: 'flex', height: 7, borderRadius: 1, overflow: 'hidden', maxWidth: 200 }}>
                {setupDist.map((setup, index) => {
                  const total = setupDist.reduce((sum, item) => sum + item.count, 0) || 1;
                  return (
                    <Tooltip key={index} title={`${getDisplayText(setup.display?.setup, t, setup.setup_type)}: ${setup.count} (${((setup.count / total) * 100).toFixed(0)}%)`} arrow>
                      <Box sx={{ width: `${(setup.count / total) * 100}%`, bgcolor: SETUP_COLORS[index % SETUP_COLORS.length], minWidth: 4 }} />
                    </Tooltip>
                  );
                })}
              </Box>
              {setupDist.map((setup, index) => {
                const total = setupDist.reduce((sum, item) => sum + item.count, 0) || 1;
                const color = SETUP_COLORS[index % SETUP_COLORS.length];
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: colors.gray[600] }}>{getDisplayText(setup.display?.setup, t, setup.setup_type)}</Typography>
                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, color }}>{setup.count}</Typography>
                    <Typography sx={{ fontSize: '0.56rem', color: colors.gray[400] }}>({((setup.count / total) * 100).toFixed(0)}%)</Typography>
                    <Typography sx={{ fontSize: '0.56rem', color: colors.gray[300] }}>{t('vsa.setup.avg')} {fmtDec(setup.avg_priority, 0)}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          {sectors.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, px: 2.5 }}>
                <BarIcon sx={{ fontSize: 14, color: colors.gray[500] }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: colors.gray[600] }}>{t('vsa.sectors.title')}</Typography>
                <Chip label={`${sectors.length}`} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(colors.info.main, 0.08), color: colors.info.main }} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thSx, width: 28 }}>{t('vsa.sectors.rank')}</TableCell>
                      <TableCell sx={thSx}>{t('vsa.sectors.sector')}</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.sectors.stocks')}</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.sectors.avgScore')}</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.sectors.rs')}</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.sectors.rsRatio')}</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.sectors.bullPct')}</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.sectors.confirmed')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectors.map((sector, index) => (
                      <TableRow key={index} sx={trSx(index, '#3b82f6')}>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: index < 3 ? colors.info.main : colors.gray[400] }}>{index + 1}</Typography></TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: index < 3 ? colors.gray[900] : colors.gray[500] }}>{sector.sector_name?.[lang] || sector.sector}</Typography></TableCell>
                        <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                          <Tooltip
                            arrow
                            placement="right"
                            componentsProps={{
                              tooltip: { sx: { bgcolor: colors.background.paper, color: colors.gray[900], boxShadow: '0 4px 24px rgba(0,0,0,0.12)', border: `1px solid ${colors.gray[200]}`, borderRadius: 2, p: 1.5, maxWidth: 320 } },
                              arrow: { sx: { color: colors.background.paper, '&::before': { border: `1px solid ${colors.gray[200]}` } } },
                            }}
                            title={sector.tickers?.length > 0 ? (
                              <Box>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.gray[700], mb: 0.8 }}>{sector.sector_name?.[lang] || sector.sector} — {sector.tickers.length} {t('vsa.sectors.stocks').toLowerCase()}</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {sector.tickers.map((ticker) => (
                                    <Chip
                                      key={ticker.ticker}
                                      label={ticker.ticker}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: ticker.grade === 'A' ? status.positive.bg : ticker.grade === 'B' ? status.info.bg : colors.gray[50], color: ticker.grade === 'A' ? status.positive.text : ticker.grade === 'B' ? status.info.text : colors.gray[500], border: `1px solid ${ticker.grade === 'A' ? status.positive.border : ticker.grade === 'B' ? status.info.border : colors.gray[200]}` }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ) : ''}
                          >
                            <Typography sx={{ fontSize: '0.72rem', color: colors.info.main, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>{sector.members}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Typography sx={{ fontSize: '0.72rem', fontWeight: 800, fontFamily: 'monospace', color: getDisplayTone(sector.display?.avg_priority?.tone).color }}>{fmtDec(sector.avg_priority, 0)}</Typography></TableCell>
                        <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: getDisplayTone(sector.display?.avg_rs?.tone).color }}>{fmtDec(sector.avg_rs, 1)}</Typography></TableCell>
                        <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: getDisplayTone(sector.display?.rs_ratio?.tone).color }}>{sector.rs_ratio != null ? `${(sector.rs_ratio * 100).toFixed(0)}%` : '—'}</Typography></TableCell>
                        <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.gray[500] }}>{sector.bull_ratio != null ? `${(sector.bull_ratio * 100).toFixed(0)}%` : '—'}</Typography></TableCell>
                        <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.gray[500] }}>{sector.confirmed_count ?? '—'}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Card>
      )}
    </>
  );
}